import { memo } from '@tanstack/table-core';

import { isDev } from '../../../../core/utils/is-node-environment.js';
import type { Row, RowModel, Table } from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';
import { ROW_EXPANDED_ID_SUFFIX } from '../RowDetails/RowDetails.js';

/**
 * Row model which reflects the order given by the rowOrder set.
 * @param rowModel - previous row model.
 * @param rowOrder - defined order of rows.
 * @returns new row model reflecting the order.
 * @internal
 */
export function getRowOrderRowModel<TData extends DataTableV2RowData>(): (
  table: Table<TData>,
) => () => RowModel<TData> {
  return (table) => {
    return memo(
      () => [
        table.getPreRowOrderRowModel(),
        table.options.enableRowOrdering,
        table.getState().rowOrder,
        table.options.state.sorting,
      ],
      (rowModel) => {
        const rowOrder = table.getState().rowOrder;
        // only order if rowOrder is enabled and we are not currently sorting
        if (
          table.options.enableRowOrdering !== true ||
          (table.options.state.sorting &&
            table.options.state.sorting.length !== 0)
        ) {
          return rowModel;
        }

        const { flatRows, rows, rowsById } = rowModel;

        if (rowOrder === undefined) {
          table.setRowOrder(flatRows.map((row) => row.id));
          return rowModel;
        }

        const orderRows = (rows: Row<TData>[]) => {
          const orderedData = [...rows];

          orderedData.sort((rowA, rowB) =>
            rowOrderCompareFn(rowOrder, rowA, rowB),
          );

          // If there are sub-rows, also order them
          orderedData.forEach((row) => {
            if (row.subRows?.length) {
              row.subRows = orderRows(row.subRows);
            }
          });

          return orderedData;
        };

        const newRowsById: typeof rowsById = {};
        Object.keys(rowsById)
          .sort((a, b) => rowOrderCompareStrFn(rowOrder, a, b))
          .reduce((obj: Record<string, Row<TData>>, key: string) => {
            obj[key] = rowsById[key];
            return obj;
          }, newRowsById);

        return {
          flatRows: orderRows(flatRows),
          rows: orderRows(rows),
          rowsById: newRowsById,
        };
      },
      {
        key: isDev() && '_getRowOrderRowModel',
        debug: () => table.options.debugAll ?? table.options.debugRows,
      },
    );
  };
}

/**
 * Compares when a and b are objects.
 * @param rowOrder - set of ids.
 * @param a - object containing id 1
 * @param b - object containing id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareFn(
  rowOrder: string[],
  a: { id: string },
  b: { id: string },
): number {
  let indexA = rowOrder.indexOf(a.id);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a.id}${ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b.id);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b.id}${ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}

/**
 * Compares when a and b are strings.
 * @param rowOrder - set of ids.
 * @param a - id 1
 * @param b - id 2
 * @returns negative, zero or positive value as required for sort function.
 */
function rowOrderCompareStrFn(
  rowOrder: string[],
  a: string,
  b: string,
): number {
  let indexA = rowOrder.indexOf(a);
  if (indexA === -1) {
    indexA = rowOrder.indexOf(`${a}${ROW_EXPANDED_ID_SUFFIX}`);
  }

  let indexB = rowOrder.indexOf(b);
  if (indexA === -1) {
    indexB = rowOrder.indexOf(`${b}${ROW_EXPANDED_ID_SUFFIX}`);
  }
  return indexA - indexB;
}
